import { defineStore } from 'pinia';
import { apiService } from '../services/apiService';
import type { User, LoginRequest, RegisterRequest, ApiError } from '../types/api';

interface AuthState {
  user: User | null;
  token: string | null;
  loading: boolean;
  error: string | null;
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    user: null,
    token: localStorage.getItem('auth_token'),
    loading: false,
    error: null
  }),

  getters: {
    isAuthenticated: (state): boolean => !!state.token && !!state.user,
    currentUser: (state): User | null => state.user
  },

  actions: {
    // Initialize auth state on app load
    async initAuth(): Promise<void> {
      this.loading = true;
      
      // Check if we have a token
      if (this.token) {
        try {
          // Verify token and get user info from the backend
          const response = await apiService.getCurrentUser();
          // Convert API response to match User type (handle null name)
          this.user = {
            id: response.user.id,
            email: response.user.email,
            name: response.user.name || undefined
          };
        } catch (error) {
          console.error('Token validation failed:', error);
          // Clear invalid token and user data
          this.logout();
        }
      }
      
      this.loading = false;
    },

    // Register new user
    async register(userData: RegisterRequest): Promise<void> {
      this.loading = true;
      this.error = null;

      try {
        const response = await apiService.register(userData);
        
        this.token = response.token;
        this.user = response.user;
        
        localStorage.setItem('auth_token', this.token);
      } catch (error) {
        this.error = (error as ApiError).error || 'Registration failed';
        throw error;
      } finally {
        this.loading = false;
      }
    },

    // Login user
    async login(credentials: LoginRequest): Promise<void> {
      this.loading = true;
      this.error = null;

      try {
        const response = await apiService.login(credentials);
        
        this.token = response.token;
        this.user = response.user;
        
        localStorage.setItem('auth_token', this.token);
      } catch (error) {
        this.error = (error as ApiError).error || 'Login failed';
        throw error;
      } finally {
        this.loading = false;
      }
    },

    // Logout user
    logout(): void {
      this.user = null;
      this.token = null;
      this.error = null;
      
      localStorage.removeItem('auth_token');
    },

    // Clear error
    clearError(): void {
      this.error = null;
    }
  }
}); 